/**
 * Where the camera previews outlive a page load: sessionStorage, a picture and its time for each
 * camera. A storage that refuses (private mode, quota) costs the previews their next page load and
 * nothing else: no caller sees a throw.
 */

import type { StoredPreview } from '@/types/devices';

import { MINUTE_MS } from './timeUnits';

const PREVIEW_PREFIX = 'cam_img_';
/** Under the prefix of the pictures, so a clear of the pictures also clears their times. */
const FETCHED_AT_PREFIX = `${PREVIEW_PREFIX}at:`;

/** A preview this recent is kept when its camera is seen online again. */
export const PREVIEW_FRESH_MS = 5 * MINUTE_MS;

export function isFreshPreview(preview: StoredPreview | null, nowMs: number): boolean {
    return preview !== null && preview.fetchedAtMs !== null &&
        nowMs - preview.fetchedAtMs < PREVIEW_FRESH_MS;
}

/** The time of a stored picture, or null when storage holds none or holds no number. */
function parseFetchedAt(storedText: string | null): number | null {
    if (storedText === null) {
        return null;
    }
    const fetchedAtMs = Number(storedText);
    return Number.isFinite(fetchedAtMs) ? fetchedAtMs : null;
}

export function readStoredPreview(id: string): StoredPreview | null {
    try {
        const dataUrl = sessionStorage.getItem(PREVIEW_PREFIX + id);
        if (!dataUrl) {
            return null;
        }
        return {
            dataUrl,
            fetchedAtMs: parseFetchedAt(sessionStorage.getItem(FETCHED_AT_PREFIX + id)),
        };
    } catch {
        return null;
    }
}

export function writeStoredPreview(id: string, preview: StoredPreview): void {
    try {
        sessionStorage.setItem(PREVIEW_PREFIX + id, preview.dataUrl);
        if (preview.fetchedAtMs === null) {
            sessionStorage.removeItem(FETCHED_AT_PREFIX + id);
        } else {
            sessionStorage.setItem(FETCHED_AT_PREFIX + id, String(preview.fetchedAtMs));
        }
    } catch {
        // A full quota: the older picture must not come back at the next page load.
        try {
            sessionStorage.removeItem(PREVIEW_PREFIX + id);
        } catch {
            // Storage is unavailable, so it keeps no picture to come back.
        }
    }
}

/** Camera ids are an organization's own, so its previews mean nothing under the next one. */
export function clearStoredPreviews(): void {
    try {
        for (const key of Object.keys(sessionStorage)) {
            if (key.startsWith(PREVIEW_PREFIX)) {
                sessionStorage.removeItem(key);
            }
        }
    } catch {
        // Storage is unavailable, so nothing remains to read.
    }
}
